import { type Dispatch, type FC, type SetStateAction } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import ImgPharmacist from '../../../../public/images/illustrations/illustration-pharmacist.png'
import { type OpenableControls } from '../../../generic-hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { StaticImage } from '../../../image'
import { type PersonalAdviceProduct } from '../../../personal-advice'
import { directContactTelephoneDialogIsPharmaCustomerSupportAvailable } from '../../directContactTelephoneDialogIsPharmaCustomerSupportAvailable'
import { useDirectContactFormWhenCustomerSupportIsUnavailable } from '../../hooks/useDirectContactFormWhenCustomerSupportIsUnavailable'
import { DirectContactFormDialog } from '../DirectContactFormDialog/DirectContactFormDialog'
import { DirectContactTelephoneUnavailable } from '../DirectContactTelephoneUnavailable'

import { DirectContactPharmaTelephoneAvailable } from './DirectContactPharmaTelephoneAvailable'

export const DirectContactPharmaTelephoneDialog: FC<{
  modalControls: OpenableControls
  onCustomerSupportUnavailableFormSubmittedWithSuccess: Dispatch<
    SetStateAction<boolean>
  >
  product?: PersonalAdviceProduct
}> = ({
  modalControls,
  onCustomerSupportUnavailableFormSubmittedWithSuccess,
  product,
}) => {
  const config = useGlobalConfigContext()
  const intl = useIntl()

  const {
    handleContactFormClick,
    handleContactFormClose,
    handleSuccess,
    hasCustomerClickedContactForm,
  } = useDirectContactFormWhenCustomerSupportIsUnavailable({
    modalControls,
    onCustomerSupportUnavailableFormSubmittedWithSuccess,
  })

  if (hasCustomerClickedContactForm) {
    const pharmaConsult = intl.formatMessage({
      id: 'contactForm.select.item.consultation',
    })

    return (
      <DirectContactFormDialog
        formSubject={pharmaConsult}
        modalControls={modalControls}
        onClose={handleContactFormClose}
        onSuccess={handleSuccess}
        product={product}
      />
    )
  }

  return (
    <Dialog onClose={modalControls.handleClose} open={modalControls.isOpen}>
      <DialogTitle onClose={modalControls.handleClose}>
        <FormattedMessage id="contact.callUsModal.title" />
      </DialogTitle>
      <DialogContent className="flex h-full flex-col items-center gap-2 p-4">
        <div className="flex w-80 flex-col items-center gap-4 text-start tablet:mx-36 tablet:text-center">
          <StaticImage
            alt="Pharmacist Illustration"
            className="h-36 w-40"
            src={ImgPharmacist}
          />
          <h3 className="w-full text-xl font-medium">
            <FormattedMessage id="contactForm.select.item.consultation" />
          </h3>
          {directContactTelephoneDialogIsPharmaCustomerSupportAvailable(
            config.shopEnv,
          ) ? (
            <DirectContactPharmaTelephoneAvailable />
          ) : (
            <DirectContactTelephoneUnavailable
              isPharmaModal
              onContactFormClick={handleContactFormClick}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
